import { matchRoutes, Navigate, useMatches } from 'react-router-dom'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { useAzureManager } from '@/auth/azure/azureManager'
import { GlobalLoadingComponent } from '@/components/GlobalLoadingComponent/GlobalLoadingComponent'
import IdleTimer from '@/components/idle-timer/IdleTimer'
import { Role } from '@/auth/azure/Roles'
import { allAuthenticatedChildrenRoutes } from '@/routes/authenticated/AuthenticatedLayout.route'

export const AuthGuard = ({ children }: { children: JSX.Element }) => {
  const matches = useMatches()
  const match = matches[matches.length - 1]

  const allMatchingRoutes = matchRoutes(allAuthenticatedChildrenRoutes, location)
  const currentRoute = allMatchingRoutes?.at(-1)?.route

  // our route metadata
  const allowedRoles: Role[] = (match?.handle as any)?.allowedRoles ?? []

  const isAuth = useIsAuthenticated()
  const { inProgress } = useMsal()
  const { userInfo } = useAzureManager()

  const now = new Date()

  const isAuthReady = inProgress === InteractionStatus.None
  const requiresRoles = allowedRoles.length > 0
  const rolesLoaded = !!userInfo?.roles

  const hasRequiredRole = currentRoute?.allowedRoles?.some((role: Role) => userInfo?.roles?.hasRole(role)) ?? false

  // 1. While auth is still initializing or we’re waiting for roles, show loader
  if (!isAuthReady || (requiresRoles && !rolesLoaded && isAuth)) {
    return <GlobalLoadingComponent />
  }

  // 2. Auth ready but not authenticated → login
  if (!isAuth) {
    return <Navigate to="/login" replace />
  }

  // 3. Authenticated but missing roles → access denied
  if (!hasRequiredRole) {
    return <Navigate to="/access-denied" replace />
  }

  // console.log('[AuthGuard] location', location.pathname)
  // console.log('[AuthGuard] currentRoute', currentRoute)
  // console.log('[AuthGuard] allowedRoles', currentRoute?.allowedRoles ?? [])
  // console.log('[AuthGuard] userInfo.roles', userInfo?.roles)
  // console.log('[AuthGuard] isAuth', isAuth)
  // console.log('[AuthGuard] hasRequiredRole', hasRequiredRole)

  // 4. Authenticated + correct roles → render children
  return (
    <>
      <IdleTimer
        timeout={60}
        startDateTime={new Date(now.getFullYear(), now.getMonth(), now.getDate(), 19, 0, 0)}
        endDateTime={new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 8, 0, 0)}
      />
      {children}
    </>
  )
}
